import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useCanGoBack, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError, type CustomerRef } from "@/lib/api";
import { changeApi } from "@/lib/changeApi";
import { pluginPointsApi } from "@/lib/pluginPointsApi";
import { useAuth } from "@/auth/AuthContext";
import { flattenQueues } from "@/components/agent/QueueTree";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { TicketPropsBar } from "@/components/agent/TicketPropsBar";
import {
  newTicketApi,
  type QueueSuggestionSource,
} from "@/lib/newTicketApi";
import {
  RecipientsField,
  joinRecipients,
  moveRecipientBetween,
  sameRecipient,
  type Recipient,
} from "@/components/agent/RecipientsField";
import { ComposerBody } from "@/components/agent/ComposerBody";
import { RefineControls } from "@/components/agent/RefineControls";
import { toneLabelKey } from "@/lib/refineTone";
import { RefineDiffView } from "@/components/agent/RefineDiffView";
import { useRefineReview } from "@/components/agent/useRefineReview";
import { CallerLookup } from "@/components/agent/phone/CallerLookup";
import { PhoneTicketFields } from "@/components/agent/phone/PhoneTicketFields";
import { useAgentOptions } from "@/lib/agentOptions";
import { PhoneCallStrip } from "@/components/agent/phone/PhoneCallStrip";
import { PhoneNoteFooter } from "@/components/agent/phone/PhoneNoteFooter";
import { DynamicFieldsForm } from "@/components/fields";
import { PendingTimeInput } from "@/components/agent/phone/PendingTimeInput";
import {
  EMPTY_PHONE_TICKET_FIELDS,
  bookedMinutes,
  type PhoneTicketFieldsValue,
} from "@/components/agent/phone/phoneTicketValue";
import { useCallTimer } from "@/components/agent/phone/useCallTimer";
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  formatBytes,
  useChatAttachments,
} from "@/components/agent/telegram/useChatAttachments";
import { ComposerEditorFrame } from "@/components/agent/ComposerEditorFrame";
import { postComposerExtras } from "@/lib/composerExtras";
import { phoneApi, type CallerCustomer, type CallerTicket } from "@/lib/phoneApi";
import {
  missingRequired,
  pendingIso,
  requestPhoneCall,
  savePhoneDraft,
  timerFromCall,
  toLocalInputValue,
} from "@/lib/phoneCall";
import { EmailSecurityControl } from "@/components/agent/EmailSecurityControl";
import { useEmailSecurity } from "@/components/agent/useEmailSecurity";
import { RecentIncidentWarning } from "@/components/itsm/RecentIncidentWarning";
import { itsmApi, itsmKeys } from "@/lib/itsmApi";


const FIELD_CLASS =
  "w-full rounded-md border border-hairline bg-surface-subtle px-3 py-2 text-[13.5px] text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent focus:border-accent";

/** Decision 4: a call logged from the popup is usually done — the first
 * closed state named "successful"/"erfolgreich" (not "unsuccessful"), else
 * the first closed one. */
function closedSuccessfulId(
  options: { id: number; name: string; type_name: string }[],
): number | undefined {
  const closed = options.filter((o) => o.type_name.startsWith("closed"));
  return (closed.find((o) => /\b(successful|erfolgreich)\b/i.test(o.name)) ?? closed[0])?.id;
}

const toggleCls =
  "inline-flex items-center gap-1 rounded border border-hairline px-2 py-0.5 text-muted transition-colors duration-100 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";
const toggleActiveCls =
  "border-accent/50 bg-accent-dim text-accent hover:text-accent";
const countBadgeCls =
  "rounded-full bg-accent-dim px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-accent";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

/** Pick a sensible default option id: the first whose name/type matches one of
 * `prefer` (case-insensitive), else the first option. Keeps the form usable
 * without hard-coding backend ids that differ per install. */
function defaultId(
  options: { id: number; name: string; type_name?: string }[],
  prefer: string[],
): number | undefined {
  for (const want of prefer) {
    const hit = options.find(
      (o) =>
        o.name.toLowerCase().includes(want) ||
        o.type_name?.toLowerCase().includes(want),
    );
    if (hit) return hit.id;
  }
  return options[0]?.id;
}

type TicketType = "email" | "phone" | "note";
type RecipientField = "to" | "cc" | "bcc";
type Direction = "inbound" | "outbound";
/** A step after the ticket's creation that failed and can run again on its own. */
type FailedStep =
  | { step: "send" | "link"; ticketId: number }
  | { step: "time"; ticketId: number; articleId: number };

const RETRY_LABEL = {
  send: "newTicket.retrySend",
  time: "newTicket.retryTime",
  link: "newTicket.retryChangeLink",
} as const;

/**
 * Agent-facing New-ticket compose page, reached from the top bar's "＋ New".
 * Customer-first flow ("Variante 2"): pick or skip a customer before the rest
 * of the form unlocks. Three ticket types: an email ticket sends a real SMTP
 * message via the same agent-reply pipeline as `ReplyDialog` (compose-context
 * resolves From/signature/rich-text per queue), a phone ticket just records a
 * customer/agent note with no email dispatch, and an internal note ticket
 * carries work that belongs to no call or mail (needs no customer; its first
 * article is an internal note, time booked on it). The queue may be
 * pre-selected via the `queue_id` search param set by the top-bar queue picker.
 */
export type NewTicketSearch = {
  queue_id?: number;
  /** Phone mode prefill (click-to-call from the customer page). */
  type?: "email" | "phone" | "note";
  direction?: "inbound" | "outbound";
  /** Customer login to preselect. */
  customer?: string;
  /** Caller / dialled number. */
  number?: string;
  /** CTI popup: epoch ms the call was answered / ended — seeds the timer. */
  call_started?: number;
  call_ended?: number;
  /** CTI popup: the agent who answered the call — the default owner. */
  owner_id?: number;
  /** Set by the CTI popup: the direction is a fact of the call (badge). */
  from_call?: boolean;
  /** "Incident aus diesem Change erstellen": the new ticket is linked to this change (ticket id). */
  from_change?: number;
  /** Subject to start with. */
  subject?: string;
};

export function NewTicketPage() {
  // A remount is the only reset that reaches every field, attachment and hook of the form.
  const [formKey, setFormKey] = useState(0);
  return <NewTicketForm key={formKey} onStartOver={() => setFormKey((k) => k + 1)} />;
}

function NewTicketForm({ onStartOver }: { onStartOver: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  // Cancel returns to where the form was opened (e.g. the customer view).
  const canGoBack = useCanGoBack();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const search = useSearch({
    from: "/agent/tickets/new",
  }) as NewTicketSearch;
  const queueId = search.queue_id;

  const queuesQ = useQuery({
    queryKey: ["queues"],
    queryFn: () => api.listQueues(),
  });
  const prioritiesQ = useQuery({
    queryKey: ["reference", "priorities"],
    queryFn: () => api.listReferencePriorities(),
  });
  const statesQ = useQuery({
    queryKey: ["reference", "states"],
    queryFn: () => api.listReferenceStates(),
  });

  const queues = useMemo(
    () => flattenQueues(queuesQ.data ?? []).filter((q) => q.valid),
    [queuesQ.data],
  );
  const priorities = useMemo(() => prioritiesQ.data ?? [], [prioritiesQ.data]);
  const [ticketType, setTicketType] = useState<TicketType>(search.type ?? "email");
  // Only offer states an agent would set on a fresh ticket (new/open), not
  // closed/removed types. A phone ticket (Znuny AgentTicketPhone) may also
  // start pending (with a time) or be closed right away; an internal note
  // ticket may be closed right away (work already done), but has no pending
  // time input.
  const states = useMemo(
    () =>
      (statesQ.data ?? []).filter((s) =>
        ticketType === "phone"
          ? s.type_name !== "removed" && s.type_name !== "merged"
          : ticketType === "note"
            ? s.type_name === "new" || s.type_name === "open" || s.type_name.startsWith("closed")
            : s.type_name === "new" ||
              s.type_name === "open" ||
              s.type_name === "pending auto",
      ),
    [statesQ.data, ticketType],
  );

  const [customer, setCustomer] = useState<CustomerRef | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [skipCustomer, setSkipCustomer] = useState(false);
  const debouncedCustomerQuery = useDebouncedValue(customerQuery, 250);
  // Plugin customer directories are slower and rate-limited: asked only after a
  // longer pause and from three characters on (D13).
  const externalCustomerQuery = useDebouncedValue(customerQuery, 400);
  const [externalError, setExternalError] = useState<string | null>(null);

  const [queue, setQueue] = useState<number | "">(queueId ?? "");
  // Owner choices follow the chosen queue (Znuny: members with owner/rw on its group, then the
  // ACLs); before a queue is known the list is empty, like Znuny.
  const ownerOptions = useAgentOptions(
    "owner",
    { action: "AgentTicketPhone", queue_id: queue === "" ? undefined : queue },
    { enabled: ticketType === "phone" && queue !== "" },
  );
  const ownerLoaded = ownerOptions.loaded;
  const ownerItems = ownerOptions.items;
  // An owner picked for another queue (or by a call prefill) that the new queue's list does not offer is dropped:
  // the default (the creator) takes over, instead of submitting a stale id.
  useEffect(() => {
    if (!ownerLoaded) return;
    setPhoneFields((f) =>
      f.ownerId !== null && !ownerItems.some((i) => i.value === f.ownerId) ? { ...f, ownerId: null } : f,
    );
  }, [ownerLoaded, ownerItems]);
  const ownerAgents = useMemo(
    () => ownerOptions.items.map((i) => ({ id: i.value, full_name: i.label, login: "" })),
    [ownerOptions.items],
  );
  // A queue the agent picked by hand (or the `?queue_id=` param) is never
  // replaced by a suggestion; otherwise the queue follows the customer.
  const [queuePinned, setQueuePinned] = useState(queueId !== undefined);
  const [subject, setSubject] = useState(search.subject ?? "");
  const [priority, setPriority] = useState<number | "">("");
  // The ITSM matrix only fills the priority until the agent picks one by hand.
  const [priorityTouched, setPriorityTouched] = useState(false);
  const [state, setState] = useState<number | "">("");
  const [body, setBody] = useState("");
  const refineReview = useRefineReview(setBody);
  const [direction, setDirection] = useState<Direction>(search.direction ?? "inbound");
  // Phone mode: caller number + lookup, the parity fields and the call timer
  // (running from page open).
  const [callerNumber, setCallerNumber] = useState(search.number ?? "");
  const [phoneFields, setPhoneFields] = useState<PhoneTicketFieldsValue>(() => ({
    ...EMPTY_PHONE_TICKET_FIELDS,
    ownerId: search.owner_id ?? null,
  }));
  // Opened from the call popup (even while still ringing): the call's
  // direction is a fact, not a choice. `direction` alone is ambiguous —
  // click-to-call from the customer page sets it too.
  const fromCall = search.from_call === true;
  const [callTimer] = useState(() => timerFromCall(search.call_started, search.call_ended));
  const timer = useCallTimer(callTimer ?? undefined);
  const attachments = useChatAttachments();
  const phoneFieldsQ = useQuery({
    queryKey: ["reference", "dynamic-fields", "AgentTicketPhone"],
    queryFn: () => phoneApi.screenDynamicFields("AgentTicketPhone"),
    enabled: ticketType === "phone",
  });
  const phoneDfs = useMemo(() => phoneFieldsQ.data ?? [], [phoneFieldsQ.data]);
  // Required fields stay visible in the main form (Decision 2); optional ones
  // go into "Weitere Felder".
  const requiredDfs = useMemo(() => phoneDfs.filter((f) => f.required), [phoneDfs]);
  const optionalDfs = useMemo(() => phoneDfs.filter((f) => !f.required), [phoneDfs]);
  // ITSM (matrix priority, incident flood hint): only while the ITSM is switched on.
  const itsmStatusQ = useQuery({
    queryKey: itsmKeys.status(),
    queryFn: ({ signal }) => itsmApi.status(signal),
    retry: false,
    staleTime: 60_000,
  });
  const itsmOn = !!itsmStatusQ.data && itsmStatusQ.data.mode !== "off";
  // The dynamic fields the matrix reads are an admin setting (default ITSMCriticality/ITSMImpact).
  const criticalityField = itsmStatusQ.data?.criticality_field ?? "";
  const impactField = itsmStatusQ.data?.impact_field ?? "";
  const hasMatrixFields =
    !!criticalityField &&
    !!impactField &&
    phoneDfs.some((f) => f.name === criticalityField) &&
    phoneDfs.some((f) => f.name === impactField);
  const criticality = phoneFields.dfValues[criticalityField]?.[0] ?? null;
  const impact = phoneFields.dfValues[impactField]?.[0] ?? null;
  const matrixQ = useQuery({
    queryKey: itsmKeys.prioritySuggestion(criticality, impact),
    queryFn: ({ signal }) => itsmApi.prioritySuggestion(criticality, impact, signal),
    retry: false,
    enabled: ticketType === "phone" && itsmOn && hasMatrixFields && !!criticality && !!impact,
  });
  // Customer preselected by login (click-to-call from the customer page).
  const presetCustomerQ = useQuery({
    queryKey: ["customers", search.customer],
    queryFn: () => api.getCustomer(search.customer as string),
    enabled: Boolean(search.customer),
  });

  const [to, setTo] = useState<Recipient[]>([]);
  const [cc, setCc] = useState<Recipient[]>([]);
  const [bcc, setBcc] = useState<Recipient[]>([]);
  const [replyTo, setReplyTo] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showReplyTo, setShowReplyTo] = useState(false);
  /** Time booked on the email's / note's article (phone keeps its own in phoneFields). */
  const [emailTimeUnits, setEmailTimeUnits] = useState("");
  /** Note: when the work was done (`datetime-local`, display zone); blank = now. */
  const [occurredAt, setOccurredAt] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [createdTicketId, setCreatedTicketId] = useState<number | null>(null);
  // The ticket exists but a step after its creation failed: only that step may run again.
  const [failedStep, setFailedStep] = useState<FailedStep | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // The search this form's ticket was submitted under: its change link belongs to that one.
  const submittedSearch = useRef(search);
  // Same route, other search ("+ New", an accepted call): the agent wants a new ticket and this
  // form's content is in the one that exists. Running steps finish first, the form stays locked.
  // Only a search change after the agent saw the failure counts: one that came before it must
  // not swallow the message that the mail did not go out.
  const searchKey = JSON.stringify(search);
  const seenFailure = useRef<{ failure: FailedStep; searchKey: string } | null>(null);
  useEffect(() => {
    if (failedStep === null) return;
    if (seenFailure.current?.failure !== failedStep) {
      seenFailure.current = { failure: failedStep, searchKey };
      return;
    }
    if (!submitting && !retrying && seenFailure.current.searchKey !== searchKey) onStartOver();
  }, [failedStep, submitting, retrying, searchKey, onStartOver]);

  // Unlocked once a customer is chosen, or the agent explicitly opts to
  // continue without one — the escape hatch button stays outside this gate.
  // Internal work rarely has a customer: a note ticket is never gated.
  const formUnlocked = customer !== null || skipCustomer || ticketType === "note";

  const customersQ = useQuery({
    queryKey: ["reference", "customer-search", debouncedCustomerQuery],
    queryFn: () => api.searchReferenceCustomers({ q: debouncedCustomerQuery }),
    enabled: !customer && debouncedCustomerQuery.trim().length >= 2,
  });

  const externalQ = useQuery({
    queryKey: ["reference", "customer-search", "external", externalCustomerQuery.trim()],
    queryFn: ({ signal }) =>
      api.customerQuickSearch({ q: externalCustomerQuery.trim(), limit: 8, external: true }, signal),
    enabled: !customer && externalCustomerQuery.trim().length >= 3,
    select: (r) => r.external ?? [],
    retry: false,
  });
  const importExternalM = useMutation({
    mutationFn: (hit: { source: string; login: string }) =>
      pluginPointsApi.externalCustomerImport(hit.source, hit.login),
    onSuccess: (c) => {
      setExternalError(null);
      selectCustomer({
        login: c.login,
        email: c.email,
        customer_id: c.customer_id,
        full_name: `${c.first_name} ${c.last_name}`.trim() || c.login,
      });
    },
    // The backend's German reason first (a 409 has seven: login taken, invalid e-mail, …);
    // the generic texts only when the response carried none.
    onError: (err) =>
      setExternalError(
        err instanceof ApiError && err.message && !err.message.startsWith("HTTP ")
          ? err.message
          : err instanceof ApiError && err.status === 409
            ? t("customers.external.conflict")
            : t("customers.external.error"),
      ),
  });

  const composeContextQ = useQuery({
    queryKey: ["reference", "compose-context", queue],
    queryFn: () => api.getComposeContext(queue as number),
    enabled: queue !== "",
  });

  // Queue default (Decision 1): the customer's last ticket, the company's,
  // the configured default, else the first non-intake queue — the backend
  // picks; before a customer is known (or "Ohne Kunde") the default applies.
  const suggestLogin = customer?.login ?? null;
  // A note ticket has no Znuny screen of its own; it follows the phone one.
  const suggestScreen = ticketType === "note" ? "phone" : ticketType;
  const suggestionQ = useQuery({
    queryKey: ["new-ticket", "queue-suggestion", suggestScreen, suggestLogin],
    queryFn: ({ signal }) =>
      suggestLogin
        ? newTicketApi.suggestedQueue(suggestLogin, suggestScreen, signal)
        : newTicketApi.defaultQueue(suggestScreen, signal),
    enabled: !queuePinned,
  });
  const suggestion = suggestionQ.data;
  useEffect(() => {
    if (queuePinned || !suggestion || suggestion.queue_id === null) return;
    if (!queues.some((q) => q.id === suggestion.queue_id)) return;
    setQueue(suggestion.queue_id);
  }, [suggestion, queuePinned, queues]);
  // Endpoint down or no usable answer: still offer a queue, but never an
  // intake one (Junk etc.).
  const suggestionFailed =
    suggestionQ.isError ||
    (suggestion !== undefined && !queues.some((q) => q.id === suggestion.queue_id));
  useEffect(() => {
    if (queue !== "" || !suggestionFailed || queues.length === 0) return;
    const intake = /^(junk|raw|postmaster)$/i;
    const pick = queues.find((q) => !intake.test(q.name.split("::")[0].trim())) ?? queues[0];
    setQueue(pick.id);
  }, [queue, suggestionFailed, queues]);

  // A `?queue_id=` the agent cannot see (gone, no permission) is dropped:
  // the queue then follows the suggestion like on a plain "New ticket".
  useEffect(() => {
    if (queues.length === 0 || queue === "" || queues.some((q) => q.id === queue)) return;
    setQueue("");
    setQueuePinned(false);
  }, [queues, queue]);

  const pickQueue = (id: number) => {
    setQueue(id);
    setQueuePinned(true);
  };
  // Only while the queue still is the suggestion for the current customer.
  const queueSource: QueueSuggestionSource | null =
    !queuePinned && suggestion && suggestion.queue_id === queue ? suggestion.source : null;
  const queueSourceText =
    queueSource === "customer" && customer
      ? t("newTicket.queueSourceCustomer", { name: customer.full_name || customer.login })
      : queueSource === "company" && customer
        ? t("newTicket.queueSourceCompany")
        : null;

  // Seed the selects with sensible defaults once their options load, without
  // clobbering a value the user already chose.
  useEffect(() => {
    if (priority === "" && priorities.length > 0) {
      setPriority(defaultId(priorities, ["normal", "3"]) ?? priorities[0].id);
    }
  }, [priorities, priority]);
  // Matrix suggestion for criticality x impact; never overrides a priority the agent picked.
  const matrixPriority = matrixQ.data?.priority_id ?? null;
  useEffect(() => {
    if (priorityTouched || matrixPriority === null) return;
    if (!priorities.some((p) => p.id === matrixPriority)) return;
    setPriority(matrixPriority);
  }, [matrixPriority, priorityTouched, priorities]);
  // Also re-seeds when the ticket type changes and the chosen state is not
  // offered for the other type (e.g. "closed" is phone-only).
  // Answered and hung up; a missed call (ended, never answered) keeps open.
  const callDone = search.call_started !== undefined && search.call_ended !== undefined;
  useEffect(() => {
    if (states.length === 0) return;
    if (state !== "" && states.some((s) => s.id === state)) return;
    const closed = ticketType === "phone" && callDone ? closedSuccessfulId(states) : undefined;
    setState(closed ?? defaultId(states, ["open", "new"]) ?? states[0].id);
  }, [states, state, ticketType, callDone]);

  const presetCustomer = presetCustomerQ.data;
  useEffect(() => {
    if (!presetCustomer) return;
    setCustomer((prev) =>
      prev ?? {
        login: presetCustomer.login,
        email: presetCustomer.email,
        customer_id: presetCustomer.customer_id,
        full_name: `${presetCustomer.first_name} ${presetCustomer.last_name}`.trim(),
      },
    );
  }, [presetCustomer]);

  // Phone mode: a customer picked from the search brings their stored number
  // along (once per customer, never over a typed one). Search hits carry no
  // phone, so the full record is read.
  const customerDetailQ = useQuery({
    queryKey: ["customers", customer?.login],
    queryFn: () => api.getCustomer(customer?.login as string),
    enabled: ticketType === "phone" && Boolean(customer?.login),
  });
  const numberPrefilledFor = useRef<string | null>(null);
  const customerDetail = customerDetailQ.data;
  useEffect(() => {
    if (!customerDetail || numberPrefilledFor.current === customerDetail.login) return;
    numberPrefilledFor.current = customerDetail.login;
    const stored = (customerDetail.phone || customerDetail.mobile || "").trim();
    if (stored) setCallerNumber((prev) => (prev.trim() ? prev : stored));
  }, [customerDetail]);

  const pickCallerCustomer = (c: CallerCustomer) =>
    selectCustomer({
      login: c.login,
      email: c.email,
      customer_id: c.customer_id,
      full_name: c.name || c.login,
    });

  /** The call is about one of the caller's open tickets: carry text and
   * timer over and log it there instead of opening a new ticket. */
  const logOnExistingTicket = (tk: CallerTicket) => {
    savePhoneDraft(tk.id, {
      direction,
      subject: subject.trim(),
      body: refineReview.flush() ?? body,
      elapsed: timer.elapsed,
    });
    requestPhoneCall(tk.id, { direction, number: callerNumber.trim() || null });
    void navigate({ to: "/agent/tickets/$ticketId", params: { ticketId: String(tk.id) } });
  };

  const selectCustomer = (c: CustomerRef) => {
    setCustomer(c);
    setCustomerQuery("");
    if (ticketType === "email") {
      const seed: Recipient = { name: c.full_name, email: c.email };
      setTo((prev) =>
        prev.some((r) => sameRecipient(r, seed)) ? prev : [...prev, seed],
      );
    }
  };

  /** Opened from a failed change: link the new ticket to it (the change's related tickets).
   * The ticket exists either way; a failed link keeps the agent here with a link to it. */
  const linkToChange = async (ticketId: number): Promise<boolean> => {
    const changeId = submittedSearch.current.from_change;
    if (!changeId) return true;
    try {
      await changeApi.addRelated(changeId, ticketId);
      return true;
    } catch {
      setCreatedTicketId(ticketId);
      setFailedStep({ step: "link", ticketId });
      setError(t("newTicket.changeLinkFailed"));
      return false;
    }
  };

  const openTicket = (ticketId: number) =>
    navigate({ to: "/agent/tickets/$ticketId", params: { ticketId: String(ticketId) } });

  /** The email ticket's steps after its creation, from the first article on (or, with
   * *articleId*, from the booking on). The ticket exists: a failed step stays here with its
   * reason, a retry of that step and the link to the ticket. */
  const finishEmailTicket = async (ticketId: number, text: string, articleId?: number) => {
    let sentArticleId = articleId;
    if (sentArticleId === undefined) {
      // The article can fail independently of ticket creation (e.g. an agent
      // email reply whose SMTP delivery fails, HTTP 502).
      try {
        ({ article_id: sentArticleId } = await api.createArticle(ticketId, {
          sender_type: "agent",
          channel: "email",
          is_visible_for_customer: true,
          subject: subject.trim(),
          body: text,
          content_type: richText
            ? "text/html; charset=utf-8"
            : "text/plain; charset=utf-8",
          to_address: joinRecipients(to),
          cc: joinRecipients(cc),
          bcc: joinRecipients(bcc),
          reply_to: replyTo.trim() || null,
          email_security: emailSecurity.payload,
          ...(attachments.payload.length > 0 ? { attachments: attachments.payload } : {}),
        }));
      } catch (articleErr) {
        setFailedStep({ step: "send", ticketId });
        // Same reason text as the reply dialog: the server's detail, unless
        // it is just the bare status line. A dropped connection is no ApiError.
        const reason = !(articleErr instanceof ApiError)
          ? null
          : articleErr.status === 413
            ? t("ticket.telegram.composer.attachTooLargeServer")
            : articleErr.message && !articleErr.message.startsWith("HTTP ")
              ? articleErr.message
              : null;
        setError(reason ? `${t("newTicket.sendError")} ${reason}` : t("newTicket.sendError"));
        return;
      }
    }
    // The mail is out; the booking follows and may fail on its own — the
    // error then keeps the link to the new ticket to book there.
    const extras = await postComposerExtras(ticketId, {
      articleId: sentArticleId,
      body: text,
      mentions: [],
      timeUnits: emailTimeUnits,
      queryClient,
    });
    if (extras.failed.length > 0) {
      setFailedStep({ step: "time", ticketId, articleId: sentArticleId });
      setError(t("ticket.extrasFailedTime"));
      return;
    }
    if (!(await linkToChange(ticketId))) return;
    // failedStep stays set: on a retry it alone keeps the create off during the navigation.
    await openTicket(ticketId);
  };

  const retryFailedStep = async () => {
    if (failedStep === null) return;
    setError(null);
    setRetrying(true);
    try {
      if (failedStep.step === "link") {
        if (!(await linkToChange(failedStep.ticketId))) return;
        await openTicket(failedStep.ticketId);
        return;
      }
      await finishEmailTicket(
        failedStep.ticketId,
        refineReview.flush() ?? body,
        failedStep.step === "time" ? failedStep.articleId : undefined,
      );
    } finally {
      setRetrying(false);
    }
  };

  const clearCustomer = () => {
    setCustomer(null);
    setCustomerQuery("");
    // Manually-entered recipients stay — only the auto-seed link is removed.
  };

  const setters: Record<RecipientField, (r: Recipient[]) => void> = {
    to: setTo,
    cc: setCc,
    bcc: setBcc,
  };
  const values: Record<RecipientField, Recipient[]> = { to, cc, bcc };

  const moveRecipient = (from: string, dest: string, r: Recipient) => {
    if (from === dest) return;
    const fromKey = from as RecipientField;
    const destKey = dest as RecipientField;
    if (!(fromKey in values) || !(destKey in values)) return;
    const { source, target } = moveRecipientBetween(
      values[fromKey],
      values[destKey],
      r,
    );
    setters[fromKey](source);
    setters[destKey](target);
    if (destKey === "cc") setShowCc(true);
    if (destKey === "bcc") setShowBcc(true);
  };

  const richText = composeContextQ.data?.rich_text ?? false;
  // A rich-text body is HTML and never reviewed: drop a review left open.
  const clearReview = refineReview.clear;
  useEffect(() => {
    if (richText) clearReview();
  }, [richText, clearReview]);

  const selectedState = statesQ.data?.find((s) => s.id === state);
  const pendingSelected = Boolean(selectedState?.type_name.startsWith("pending"));
  // Decision 4: a phone ticket logged as already done says so on the button.
  const closedSelected = Boolean(selectedState?.type_name.startsWith("closed"));
  const phoneReady =
    missingRequired(phoneDfs, phoneFields.dfValues).length === 0 &&
    (!pendingSelected || pendingIso(phoneFields.pendingAt) !== null);

  // PGP / S/MIME for the email (separate component; nothing shown when off).
  const emailSecurity = useEmailSecurity({
    ticketId: null,
    queueId: queue === "" ? null : queue,
    to: joinRecipients(to),
    cc: joinRecipients(cc),
    bcc: joinRecipients(bcc),
    enabled: ticketType === "email",
  });

  // The server takes the past year only; a typed date outside it would 422.
  const occurredIso = occurredAt ? pendingIso(occurredAt) : null;
  const occurredValid =
    !occurredAt ||
    (occurredIso !== null &&
      Date.parse(occurredIso) <= Date.now() &&
      Date.parse(occurredIso) >= Date.now() - 365 * 86_400_000);

  // The retried mail goes out from the form as it stands now: it must still be sendable.
  const canResend =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    to.length > 0 &&
    !attachments.encoding &&
    emailSecurity.blocked === null;

  const canSubmit =
    (ticketType !== "email" || emailSecurity.blocked === null) &&
    (ticketType !== "note" || occurredValid) &&
    formUnlocked &&
    queue !== "" &&
    priority !== "" &&
    state !== "" &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (ticketType !== "email" || to.length > 0) &&
    (ticketType !== "phone" || phoneReady) &&
    // A file still being read would silently go missing from the request.
    !attachments.encoding &&
    // The ticket exists already (only a later step failed): no second create.
    failedStep === null &&
    !retrying &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (failedStep !== null) return;
    submittedSearch.current = search;
    setError(null);
    setCreatedTicketId(null);
    // `canSubmit` already asserts queue/priority/state are set (TS narrows
    // them to `number` below via aliased-condition control-flow analysis).
    if (!canSubmit || !user) {
      setError(t("newTicket.validationError"));
      return;
    }
    // Submitting while a refine review is open = accept as marked.
    const text = refineReview.flush() ?? body;
    setSubmitting(true);
    try {
      if (ticketType === "phone") {
        // One request: ticket + the call as first article (+ auto reply).
        const minutes = bookedMinutes(phoneFields, timer.elapsed);
        const { ticket_id: phoneTicketId } = await api.createTicket({
          title: subject.trim(),
          queue_id: queue,
          state_id: state,
          priority_id: priority,
          owner_id: phoneFields.ownerId ?? user.id,
          responsible_id: phoneFields.responsibleId,
          type_id: phoneFields.typeId,
          service_id: phoneFields.serviceId,
          sla_id: phoneFields.slaId,
          customer_id: customer?.customer_id || null,
          customer_user_id: customer?.login ?? null,
          dynamic_fields: Object.fromEntries(
            Object.entries(phoneFields.dfValues).filter(([, v]) => v.length > 0),
          ),
          pending_time: pendingSelected ? pendingIso(phoneFields.pendingAt) : null,
          phone_call: {
            direction,
            body: text,
            content_type: "text/plain",
            is_visible_for_customer: true,
            time_unit: minutes,
            attachments: attachments.payload,
            caller_number: callerNumber.trim() || null,
          },
          send_auto_response: direction === "inbound",
        });
        if (!(await linkToChange(phoneTicketId))) return;
        await navigate({
          to: "/agent/tickets/$ticketId",
          params: { ticketId: String(phoneTicketId) },
        });
        return;
      }
      if (ticketType === "note") {
        // One request: ticket + internal note + booking, all dated to when
        // the work was done (or now).
        const units = Number(emailTimeUnits);
        const { ticket_id: noteTicketId } = await api.createTicket({
          title: subject.trim(),
          queue_id: queue,
          state_id: state,
          priority_id: priority,
          owner_id: user.id,
          customer_id: customer?.customer_id || null,
          customer_user_id: customer?.login ?? null,
          note: {
            body: text,
            content_type: richText ? "text/html" : "text/plain",
            time_unit: emailTimeUnits.trim() !== "" && units > 0 ? units : null,
            attachments: attachments.payload,
          },
          occurred_at: occurredIso,
        });
        if (!(await linkToChange(noteTicketId))) return;
        await navigate({
          to: "/agent/tickets/$ticketId",
          params: { ticketId: String(noteTicketId) },
        });
        return;
      }
      const { ticket_id } = await api.createTicket({
        title: subject.trim(),
        queue_id: queue,
        state_id: state,
        priority_id: priority,
        owner_id: user.id,
        customer_user_id: customer?.login ?? null,
      });
      setCreatedTicketId(ticket_id);
      await finishEmailTicket(ticket_id, text);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t("newTicket.submitError"));
      } else {
        throw err;
      }
    } finally {
      setSubmitting(false);
    }
  };

  // No quote here, so the whole body is the agent's own text. Phone: in the
  // note's footer bar; e-mail: under the message.
  const refineControls = (
    <RefineControls
      target={
        queue === ""
          ? null
          : {
              queue_id: queue,
              customer_user_id: customer?.login ?? null,
            }
      }
      body={body}
      onChange={refineReview.onEdit}
      testIdPrefix="new-ticket-refine"
      // A rich-text body is HTML: a word diff of markup is unreadable,
      // so that case applies the refined text directly as before.
      onRefined={
        ticketType === "email" && richText ? undefined : refineReview.onRefined
      }
      appliedStats={refineReview.applied?.stats ?? null}
      onShowChanges={refineReview.showChanges}
      reviewOpen={refineReview.review !== null}
      mode={ticketType === "phone" ? "call_note" : "message"}
      // E-mail: in the editor frame's bottom bar, like the reply dialog.
      variant={ticketType === "email" ? "toolbar" : undefined}
    />
  );

  const loading =
    queuesQ.isLoading || prioritiesQ.isLoading || statesQ.isLoading;

  const ccOn = showCc || cc.length > 0;
  const bccOn = showBcc || bcc.length > 0;
  const replyToCount = replyTo.trim() ? 1 : 0;
  const replyToOn = showReplyTo || replyToCount > 0;

  return (
    <div
      className="mx-auto w-full max-w-3xl px-4 py-6"
      data-testid="agent-new-ticket-page"
    >
      <h1 className="font-display text-xl font-semibold text-ink">
        {t("newTicket.title")}
      </h1>
      <p className="mt-1 text-[13px] text-muted">{t("newTicket.intro")}</p>
      {search.from_change && (
        <p className="mt-2 rounded border border-hairline bg-surface-subtle px-3 py-2 text-[13px] text-ink" data-testid="new-ticket-from-change">
          {t("newTicket.fromChange", { id: search.from_change })}
        </p>
      )}

      <div className="mt-5 inline-flex overflow-hidden rounded-md border border-hairline text-sm">
        <button
          type="button"
          data-testid="new-ticket-type-email"
          aria-pressed={ticketType === "email"}
          onClick={() => setTicketType("email")}
          className={cn(
            "px-3 py-1.5",
            ticketType === "email"
              ? "bg-accent text-accent-ink"
              : "bg-surface text-muted hover:text-ink",
          )}
        >
          {t("newTicket.typeEmail")}
        </button>
        <button
          type="button"
          data-testid="new-ticket-type-phone"
          aria-pressed={ticketType === "phone"}
          onClick={() => setTicketType("phone")}
          className={cn(
            "border-l border-hairline px-3 py-1.5",
            ticketType === "phone"
              ? "bg-accent text-accent-ink"
              : "bg-surface text-muted hover:text-ink",
          )}
        >
          {t("newTicket.typePhone")}
        </button>
        <button
          type="button"
          data-testid="new-ticket-type-note"
          aria-pressed={ticketType === "note"}
          onClick={() => setTicketType("note")}
          className={cn(
            "border-l border-hairline px-3 py-1.5",
            ticketType === "note"
              ? "bg-accent text-accent-ink"
              : "bg-surface text-muted hover:text-ink",
          )}
        >
          {t("newTicket.typeNote")}
        </button>
      </div>
      {ticketType === "note" && (
        <p className="mt-2 text-[12px] text-muted" data-testid="new-ticket-note-hint">
          {t("newTicket.typeNoteHint")}
        </p>
      )}

      {loading ? (
        <div className="mt-6 flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="mt-4 space-y-4">
          <div className="space-y-3 rounded-xl border border-hairline bg-surface p-4">
            {ticketType === "phone" && (
              <PhoneCallStrip
                direction={direction}
                onDirectionChange={setDirection}
                fixed={fromCall}
                number={callerNumber}
                answeredAt={search.call_started}
                endedAt={search.call_ended}
                elapsed={timer.elapsed}
                running={timer.running}
                onToggleTimer={timer.running ? timer.pause : timer.resume}
                hasCustomer={customer !== null}
                queueAutoReply={composeContextQ.data?.auto_reply}
              />
            )}
            {ticketType === "phone" && (
              <CallerLookup
                number={callerNumber}
                onNumberChange={setCallerNumber}
                showCustomers={customer === null}
                onPickCustomer={pickCallerCustomer}
                onLogOnTicket={logOnExistingTicket}
              />
            )}
            {customer ? (
              <div
                className="flex items-center gap-2 rounded border border-hairline bg-surface-subtle px-3 py-2 text-sm"
                data-testid="new-ticket-customer-card"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">
                    {customer.full_name}
                  </p>
                  <p className="truncate text-xs text-muted">
                    {customer.email} · {customer.login}
                  </p>
                </div>
                {customer.customer_id && (
                  <Badge tone="muted">{customer.customer_id}</Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="new-ticket-customer-clear"
                  onClick={clearCustomer}
                >
                  {t("newTicket.customerChange")}
                </Button>
              </div>
            ) : (
              <>
                <input
                  data-testid="new-ticket-customer-search"
                  value={customerQuery}
                  autoFocus
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    // an import error belongs to the hits it was raised under
                    setExternalError(null);
                  }}
                  placeholder={t("newTicket.customerSearch")}
                  className={FIELD_CLASS}
                />
                <p className="mt-1 text-[11px] text-muted">
                  {t("newTicket.customerSearchHint")}
                </p>
                {debouncedCustomerQuery.trim().length >= 2 && (
                  <div className="mt-2 max-h-56 overflow-auto rounded border border-hairline">
                    {customersQ.isLoading ? (
                      <div className="flex justify-center py-3">
                        <Spinner />
                      </div>
                    ) : (customersQ.data ?? []).length === 0 ? (
                      <p className="px-3 py-2 text-xs text-muted">
                        {t("newTicket.noCustomerResults")}
                      </p>
                    ) : (
                      (customersQ.data ?? []).map((c) => (
                        <button
                          key={c.login}
                          type="button"
                          data-testid={`new-ticket-customer-result-${c.login}`}
                          onClick={() => selectCustomer(c)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-ink hover:bg-surface-subtle"
                        >
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{c.full_name}</span>{" "}
                            <span className="text-muted">{c.email}</span>
                          </span>
                          {c.customer_id && (
                            <Badge tone="muted" className="ml-auto shrink-0">
                              {c.customer_id}
                            </Badge>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
                {(externalQ.data ?? []).length > 0 && debouncedCustomerQuery.trim().length >= 2 && (
                  <div
                    className="mt-2 max-h-56 overflow-auto rounded border border-hairline"
                    data-testid="new-ticket-customer-external"
                  >
                    <p className="border-b border-hairline bg-surface-subtle px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                      {t("customers.external.title")}
                    </p>
                    {(externalQ.data ?? []).map((x) => (
                      <div
                        key={`${x.source}:${x.login}`}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-ink"
                        data-testid={`new-ticket-customer-external-${x.login}`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">
                            {`${x.first_name} ${x.last_name}`.trim() || x.login}
                          </span>{" "}
                          <span className="text-muted">{x.email}</span>
                        </span>
                        <Badge tone="muted" className="shrink-0">
                          {t("customers.external.source", { label: x.source_label })}
                        </Badge>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={importExternalM.isPending}
                          data-testid={`new-ticket-customer-import-${x.login}`}
                          onClick={() => importExternalM.mutate({ source: x.source, login: x.login })}
                        >
                          {t("customers.external.import")}
                        </Button>
                      </div>
                    ))}
                    {externalError && (
                      <p className="px-3 py-1 text-xs text-danger" role="alert" data-testid="new-ticket-customer-external-error">
                        {externalError}
                      </p>
                    )}
                  </div>
                )}
                {!formUnlocked && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    data-testid="new-ticket-skip-customer"
                    onClick={() => setSkipCustomer(true)}
                  >
                    {t("newTicket.skipCustomer")}
                  </Button>
                )}
              </>
            )}
          </div>

          <fieldset
            disabled={!formUnlocked}
            className={cn(
              "min-w-0 space-y-4 rounded-xl border border-hairline bg-surface p-5",
              !formUnlocked && "pointer-events-none opacity-50",
            )}
          >
            {ticketType === "email" && (
              <div className="block">
                <span className="mb-1 block text-[12px] font-medium text-muted">
                  {t("newTicket.from")}
                </span>
                <p
                  className="rounded-md border border-hairline bg-surface-subtle px-3 py-2 text-[13.5px] text-muted"
                  data-testid="new-ticket-from"
                >
                  {composeContextQ.isLoading
                    ? "…"
                    : (composeContextQ.data?.from_address ?? "—")}
                </p>
              </div>
            )}

            {ticketType === "email" && (
              <div className="space-y-2">
                <RecipientsField
                  label={t("ticket.replyTo")}
                  fieldKey="to"
                  recipients={to}
                  onChange={setTo}
                  onMove={moveRecipient}
                  required
                  placeholder={t("ticket.recipientAddHint")}
                  testid="new-ticket-to"
                />
                {showCc && (
                  <RecipientsField
                    label={t("ticket.replyCc")}
                    fieldKey="cc"
                    recipients={cc}
                    onChange={setCc}
                    onMove={moveRecipient}
                    placeholder={t("ticket.recipientAddHint")}
                    testid="new-ticket-cc"
                  />
                )}
                {showBcc && (
                  <RecipientsField
                    label={t("ticket.replyBcc")}
                    fieldKey="bcc"
                    recipients={bcc}
                    onChange={setBcc}
                    onMove={moveRecipient}
                    placeholder={t("ticket.recipientAddHint")}
                    testid="new-ticket-bcc"
                  />
                )}
                {showReplyTo && (
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-medium text-muted">
                      {t("ticket.replyReplyTo")}
                    </span>
                    <input
                      className={FIELD_CLASS}
                      value={replyTo}
                      data-testid="new-ticket-replyto"
                      placeholder={t("ticket.recipientAddHint")}
                      onChange={(e) => setReplyTo(e.target.value)}
                    />
                  </label>
                )}
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <button
                    type="button"
                    className={cn(toggleCls, ccOn && toggleActiveCls)}
                    data-testid="new-ticket-toggle-cc"
                    data-active={ccOn ? "true" : "false"}
                    aria-expanded={showCc}
                    onClick={() => setShowCc((v) => !v)}
                  >
                    {t("ticket.replyCc")}
                    {!showCc && cc.length > 0 && (
                      <span className={countBadgeCls}>{cc.length}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={cn(toggleCls, bccOn && toggleActiveCls)}
                    data-testid="new-ticket-toggle-bcc"
                    data-active={bccOn ? "true" : "false"}
                    aria-expanded={showBcc}
                    onClick={() => setShowBcc((v) => !v)}
                  >
                    {t("ticket.replyBcc")}
                    {!showBcc && bcc.length > 0 && (
                      <span className={countBadgeCls}>{bcc.length}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={cn(toggleCls, replyToOn && toggleActiveCls)}
                    data-testid="new-ticket-toggle-replyto"
                    data-active={replyToOn ? "true" : "false"}
                    aria-expanded={showReplyTo}
                    onClick={() => setShowReplyTo((v) => !v)}
                  >
                    {t("ticket.replyReplyTo")}
                    {!showReplyTo && replyToCount > 0 && (
                      <span className={countBadgeCls}>{replyToCount}</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-muted">
                {t("newTicket.subject")}
              </span>
              <input
                data-testid="new-ticket-subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={FIELD_CLASS}
              />
            </label>

            {ticketType === "note" && (
              <label className="block">
                <span className="mb-1 block text-[12px] font-medium text-muted">
                  {t("newTicket.occurredAt")}
                </span>
                <input
                  type="datetime-local"
                  data-testid="new-ticket-occurred-at"
                  value={occurredAt}
                  max={toLocalInputValue(new Date())}
                  onChange={(e) => setOccurredAt(e.target.value)}
                  aria-invalid={!occurredValid}
                  aria-describedby="new-ticket-occurred-at-hint"
                  className={cn(FIELD_CLASS, "w-auto")}
                />
                <span
                  id="new-ticket-occurred-at-hint"
                  className={cn("mt-1 block text-[12px]", occurredValid ? "text-muted" : "text-danger")}
                >
                  {occurredValid ? t("newTicket.occurredAtHint") : t("newTicket.occurredAtInvalid")}
                </span>
              </label>
            )}

            <TicketPropsBar
              queues={queues}
              queueId={queue === "" ? null : queue}
              onQueueChange={pickQueue}
              queueSource={queueSourceText}
              owner={
                ticketType === "phone"
                  ? {
                      agents: ownerAgents,
                      value: phoneFields.ownerId ?? user?.id ?? null,
                      fallbackName:
                        (phoneFields.ownerId ?? user?.id) === user?.id
                          ? [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.login
                          : `#${phoneFields.ownerId}`,
                      onChange: (id) => setPhoneFields((f) => ({ ...f, ownerId: id })),
                      source:
                        search.owner_id !== undefined && phoneFields.ownerId === search.owner_id
                          ? t("newTicket.ownerSourceCall")
                          : null,
                    }
                  : undefined
              }
              priorities={priorities}
              priorityId={priority === "" ? null : priority}
              onPriorityChange={(id) => {
                setPriorityTouched(true);
                setPriority(id);
              }}
              states={states}
              stateId={state === "" ? null : state}
              onStateChange={setState}
            />

            {ticketType === "phone" && pendingSelected && (
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="new-ticket-pending">
                <span className="text-muted">{t("phone.pendingUntil")}</span>
                <PendingTimeInput
                  value={phoneFields.pendingAt}
                  onChange={(v) => setPhoneFields((f) => ({ ...f, pendingAt: v }))}
                  testId="new-ticket-pending-time"
                />
              </div>
            )}

            {ticketType === "phone" && (
              <DynamicFieldsForm
                fields={requiredDfs}
                values={phoneFields.dfValues}
                onChange={(name, values) =>
                  setPhoneFields((f) => ({ ...f, dfValues: { ...f.dfValues, [name]: values } }))
                }
                testId="new-ticket-df-required"
              />
            )}

            {ticketType === "phone" && (
              <PhoneTicketFields
                value={phoneFields}
                onChange={setPhoneFields}
                fields={optionalDfs}
                queueId={queue === "" ? null : queue}
              />
            )}

            <RecentIncidentWarning
              serviceId={ticketType === "phone" ? phoneFields.serviceId : null}
              enabled={itsmOn}
            />

            {ticketType === "phone" ? (
              <div className="block">
                <label
                  // Only while the textarea exists (not during a review).
                  htmlFor={refineReview.review ? undefined : "new-ticket-note"}
                  className="mb-1 block text-[12px] font-medium text-muted"
                >
                  {t("newTicket.note")}
                </label>
                <div className="overflow-hidden rounded-lg border border-hairline bg-surface focus-within:border-accent">
                  {refineReview.review ? (
                    <RefineDiffView
                      key={refineReview.reviewKey}
                      before={refineReview.review.before}
                      after={refineReview.review.after}
                      toneLabel={t(toneLabelKey(refineReview.review.tone))}
                      onAccept={refineReview.accept}
                      onDiscard={refineReview.discard}
                      onGroupsChange={refineReview.onGroupsChange}
                      className="min-h-[12rem]"
                    />
                  ) : (
                    <textarea
                      id="new-ticket-note"
                      data-testid="new-ticket-body"
                      value={body}
                      spellCheck={false}
                      onChange={(e) => refineReview.onEdit(e.target.value)}
                      className="block min-h-[190px] w-full resize-y border-0 bg-surface px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-ink focus-visible:outline-none"
                    />
                  )}
                  <PhoneNoteFooter
                    refine={refineControls}
                    elapsed={timer.elapsed}
                    timeUnits={phoneFields.timeUnits}
                    onTimeUnitsChange={(v) => setPhoneFields((f) => ({ ...f, timeUnits: v }))}
                    attachments={attachments.items}
                    onAttach={attachments.add}
                    onRemoveAttachment={attachments.remove}
                  />
                </div>
              </div>
            ) : (
              <div className="block">
                <span className="mb-1 block text-[12px] font-medium text-muted">
                  {ticketType === "note" ? t("newTicket.internalNote") : t("newTicket.message")}
                </span>
                {/* Same framed editor as the reply dialog: text, signature,
                    files and the refine/file/time toolbar. */}
                <ComposerEditorFrame
                  testIdPrefix="new-ticket"
                  attachments={attachments}
                  timeUnits={emailTimeUnits}
                  onTimeUnitsChange={setEmailTimeUnits}
                  signature={
                    ticketType === "email" && composeContextQ.data
                      ? {
                          text: composeContextQ.data.signature,
                          isHtml: composeContextQ.data.signature_is_html,
                        }
                      : null
                  }
                  refine={refineControls}
                >
                  {refineReview.review ? (
                    <RefineDiffView
                      key={refineReview.reviewKey}
                      before={refineReview.review.before}
                      after={refineReview.review.after}
                      toneLabel={t(toneLabelKey(refineReview.review.tone))}
                      onAccept={refineReview.accept}
                      onDiscard={refineReview.discard}
                      onGroupsChange={refineReview.onGroupsChange}
                      className="min-h-[15rem]"
                    />
                  ) : (
                    <ComposerBody
                      richText={richText}
                      value={body}
                      onChange={refineReview.onEdit}
                      testId="new-ticket-body"
                      bare
                    />
                  )}
                </ComposerEditorFrame>
              </div>
            )}

            {/* The e-mail frame shows its own; the phone footer has no room. */}
            {ticketType === "phone" && attachments.error && (
              <p className="text-xs text-danger" data-testid="new-ticket-attach-error">
                {attachments.error === "tooLarge"
                  ? t("ticket.telegram.composer.attachTooLarge", {
                      max: formatBytes(MAX_ATTACHMENT_BYTES),
                    })
                  : attachments.error === "tooMany"
                    ? t("ticket.telegram.composer.attachTooMany", { max: MAX_ATTACHMENTS })
                    : t("ticket.telegram.composer.attachReadFailed")}
              </p>
            )}
          </fieldset>

          {ticketType === "email" && (
            <EmailSecurityControl
              security={emailSecurity}
              testId="new-ticket-security"
            />
          )}

          {error && (
            <p
              className="text-[13px] text-danger"
              data-testid="new-ticket-error"
              role="alert"
            >
              {error}
              {failedStep !== null && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="underline disabled:opacity-50"
                    disabled={retrying || (failedStep.step === "send" && !canResend)}
                    onClick={() => void retryFailedStep()}
                    data-testid={`new-ticket-retry-${failedStep.step}`}
                  >
                    {t(RETRY_LABEL[failedStep.step])}
                  </button>
                </>
              )}
              {createdTicketId && (
                <>
                  {" "}
                  <Link
                    to="/agent/tickets/$ticketId"
                    params={{ ticketId: String(createdTicketId) }}
                    className="underline"
                  >
                    {t("newTicket.goToTicket")}
                  </Link>
                </>
              )}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (canGoBack ? router.history.back() : void navigate({ to: "/agent" }))}
              data-testid="new-ticket-cancel"
            >
              {t("newTicket.cancel")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              data-testid="new-ticket-submit"
            >
              {submitting ? (
                <Spinner />
              ) : ticketType === "email" ? (
                t("newTicket.submitSend")
              ) : ticketType === "note" && closedSelected ? (
                t("newTicket.submitNoteClose")
              ) : closedSelected ? (
                t("newTicket.submitClose")
              ) : (
                t("newTicket.submit")
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
